<script setup>
import {nextTick, onMounted, ref} from 'vue';
import {Head, useForm} from '@inertiajs/inertia-vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import SecondaryButton from "@/Components/SecondaryButton.vue";

const providers = [
  {
    name: 'Google',
    identifier: '2fa',
    description: 'When two factor authentication is enabled, you will be prompted for a secure, random token during authentication. You may retrieve this token from your phone\'s Google Authenticator application.'
  },
  {
    name: 'SMS',
    identifier: 'sms',
    description: 'When two factor authentication is enabled, you will be prompted for a secure, random token during authentication. You may retrieve this token from a SMS on your phone number.'
  }
];

const props = defineProps({
  mfa: Object | null
});

const recovery = ref(false);

const form = useForm({
  provider: null,
  code: '',
  recovery_code: '',
});

const recoveryCodeInput = ref(null);
const codeInput = ref(null);
const challenge = ref(null);

const toggleRecovery = async () => {
  recovery.value ^= true;

  await nextTick();

  if (recovery.value) {
    recoveryCodeInput.value.focus();
    form.code = '';
  } else {
    codeInput.value.focus();
    form.recovery_code = '';
  }
};

const challengeSms = async () => {
  return axios.post(route('login.two-factor-challenge.challenge'), {provider: form.provider}).then(response => {
    challenge.value = response.data.challenge;
  });
}

onMounted(() => {
  if (!props.mfa) {
    return;
  }

  Object.keys(props.mfa).forEach(provider => {
    if (isProviderEnabled(provider)) {
      form.provider = provider;
      return false;
    }
  })
});

const isProviderEnabled = (provider) => {
  return props.mfa[provider]?.is_enabled === true;
}

const submit = () => {
  form.post(route('login.two-factor-challenge.store'), {
    errorBag: 'confirmTwoFactorAuthentication',
  });
};
</script>

<template>
  <Head title="Two-factor Confirmation"/>

  <AuthenticationCard>
    <template #logo>
      <AuthenticationCardLogo/>
    </template>

    <div class="mb-4 text-sm text-gray-600">
      <template v-if="! recovery">
        Please confirm access to your account by entering the authentication code provided by your authenticator
        application.
      </template>

      <template v-else>
        Please confirm access to your account by entering one of your emergency recovery codes.
      </template>
    </div>

    <div class="mb-4 text-sm text-gray-600">
      <template v-for="provider in providers">
        <label class="mr-5" v-if="isProviderEnabled(provider.identifier)">
          <input type="radio" v-model="form.provider" :value="provider.identifier"
                 :disabled="!isProviderEnabled(provider.identifier)">
          <span class="ml-1" v-text="provider.name"></span>
        </label>
      </template>
      <p v-if="form.provider === 'sms' && !challenge" class="mt-4">
        <SecondaryButton @click="challengeSms">Send SMS Code</SecondaryButton>
      </p>
      <p class="mt-3" v-if="challenge" v-text="challenge"></p>
    </div>

    <form @submit.prevent="submit" v-if="form.provider !== null">
      <div v-if="! recovery">
        <InputLabel for="code" value="Code"/>
        <TextInput
          id="code"
          ref="codeInput"
          v-model="form.code"
          type="text"
          inputmode="numeric"
          class="mt-1 block w-full"
          autofocus
          autocomplete="one-time-code"
        />
        <InputError class="mt-2" :message="form.errors.code"/>
      </div>

      <div v-else>
        <InputLabel for="recovery_code" value="Recovery Code"/>
        <TextInput
          id="recovery_code"
          ref="recoveryCodeInput"
          v-model="form.recovery_code"
          type="text"
          class="mt-1 block w-full"
          autocomplete="one-time-code"
        />
        <InputError class="mt-2" :message="form.errors.recovery_code"/>
      </div>

      <div class="flex items-center justify-end mt-4">
        <button type="button" class="text-sm text-gray-600 hover:text-gray-900 underline cursor-pointer"
                @click.prevent="toggleRecovery">
          <template v-if="! recovery">
            Use a recovery code
          </template>

          <template v-else>
            Use an authentication code
          </template>
        </button>

        <PrimaryButton class="ml-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
          Log in
        </PrimaryButton>
      </div>
    </form>
  </AuthenticationCard>
</template>
